// ============================================================================
// Enums / Union Types
// ============================================================================

export const DRIVE_ROOT_FOLDER_ID = '0AIfT0LuZgJCyUk9PVA';

export type NodeType = 'drive_input' | 'claude_vision' | 'output_designer' | 'sheet_output' | 'drive_move';
export type ExecutionStatus = 'pending' | 'running' | 'completed' | 'failed' | 'partial' | 'pending_approval';
export type FieldType = 'string' | 'number' | 'date' | 'boolean';

// ============================================================================
// Constants for UI (Labels, Colors)
// ============================================================================

export const NODE_TYPE_LABELS: Record<NodeType, string> = {
  drive_input: 'Drive Input',
  claude_vision: 'Claude Vision',
  output_designer: 'Output Designer',
  sheet_output: 'Sheet Output',
  drive_move: 'Drive Move',
};

export const NODE_TYPE_COLORS: Record<NodeType, {
  bg: string;
  border: string;
  text: string;
  badge: string;
}> = {
  drive_input: {
    bg: 'bg-blue-50 dark:bg-blue-950',
    border: 'border-blue-500',
    text: 'text-blue-700 dark:text-blue-300',
    badge: 'bg-blue-500 text-white',
  },
  claude_vision: {
    bg: 'bg-purple-50 dark:bg-purple-950',
    border: 'border-purple-500',
    text: 'text-purple-700 dark:text-purple-300',
    badge: 'bg-purple-500 text-white',
  },
  output_designer: {
    bg: 'bg-amber-50 dark:bg-amber-950',
    border: 'border-amber-500',
    text: 'text-amber-700 dark:text-amber-300',
    badge: 'bg-amber-500 text-white',
  },
  sheet_output: {
    bg: 'bg-green-50 dark:bg-green-950',
    border: 'border-green-500',
    text: 'text-green-700 dark:text-green-300',
    badge: 'bg-green-500 text-white',
  },
  drive_move: {
    bg: 'bg-cyan-50 dark:bg-cyan-950',
    border: 'border-cyan-500',
    text: 'text-cyan-700 dark:text-cyan-300',
    badge: 'bg-cyan-500 text-white',
  },
};

export const EXECUTION_STATUS_LABELS: Record<ExecutionStatus, string> = {
  pending: 'Pending',
  running: 'Running',
  completed: 'Completed',
  failed: 'Failed',
  partial: 'Partial',
  pending_approval: 'Pending Approval',
};

export const EXECUTION_STATUS_COLORS: Record<ExecutionStatus, string> = {
  pending: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
  running: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  completed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  failed: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  partial: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  pending_approval: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
};

export const FIELD_TYPE_OPTIONS: { value: FieldType; label: string }[] = [
  { value: 'string', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'boolean', label: 'Boolean' },
];

// ============================================================================
// Core Interfaces
// ============================================================================

export interface IOutputField {
  key: string;
  label: string;
  type: FieldType;
  required: boolean;
}

export interface INodePosition {
  x: number;
  y: number;
}

export interface INodeConfig {
  // drive_input
  folder_id?: string;
  folder_name?: string;
  file_filter?: string; // "all" | "pdf" | "image"

  // claude_vision
  prompt?: string;
  model?: string;
  tw_invoice_mode?: boolean;
  tw_invoice_type?: string;

  // output_designer
  template_name?: string;
  fields?: IOutputField[];

  // sheet_output
  spreadsheet_id?: string;
  sheet_name?: string;
  header_row?: boolean;

  // drive_move
  destination_folder_id?: string;
  destination_folder_name?: string;
}

export interface IWorkflowNode {
  id: string;
  type: NodeType;
  label: string;
  position: INodePosition;
  config: INodeConfig;
}

export interface IWorkflowEdge {
  id: string;
  source: string;
  target: string;
}

export interface IWorkflow {
  id: string;
  name: string;
  description: string;
  nodes: IWorkflowNode[];
  edges: IWorkflowEdge[];
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface IExecution {
  id: string;
  workflow_id: string;
  status: ExecutionStatus;
  file_ids: string[];
  total_files: number;
  done_files: number;
  failed_files: number;
  error?: string;
  started_at: string;
  completed_at?: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface IExecutionResult {
  id: string;
  execution_id: string;
  file_id: string;
  file_name: string;
  status: ExecutionStatus;
  extracted_data?: Record<string, unknown>;
  node_outputs?: Record<string, Record<string, unknown>>;
  error?: string;
  created_at: string;
}

export interface IDriveFile {
  id: string;
  name: string;
  mime_type: string;
  size?: number;
  web_view_link?: string;
  created_at?: string;
}

export interface ITemplate {
  id: string;
  name: string;
  description: string;
  fields: IOutputField[];
}

// ============================================================================
// Request Types
// ============================================================================

export interface ICreateWorkflowRequest {
  name: string;
  description?: string;
  nodes?: IWorkflowNode[];
  edges?: IWorkflowEdge[];
}

export interface IUpdateWorkflowRequest {
  name?: string;
  description?: string;
  nodes?: IWorkflowNode[];
  edges?: IWorkflowEdge[];
}

export interface IRunWorkflowRequest {
  file_ids: string[];
}

export interface IApprovedResult {
  result_id: string;
  extracted_data: Record<string, unknown>;
}

export interface IApproveExecutionRequest {
  results: IApprovedResult[];
}
